import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, ListGroup, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Eye, EyeSlash, XCircle, FileEarmarkText } from 'react-bootstrap-icons';
import { useFileUpload } from 'hooks/useFileUpload';

/* ---------------------------------------------------------------------
   EditableField Component
------------------------------------------------------------------------ */
const EditableField = ({
  label,
  name,
  value,
  error,
  onChange,
  onSave,
  originalValue,
  type = 'text',
  options,
  isSaving,
  isEditMode,
  disabled,
  placeholder,
  appendContent
}) => {
  const selectPlaceholder = placeholder || 'Select...';
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');

  useEffect(() => setInternalValue(value || ''), [value]);

  const toggleEdit = async () => {
    if (isEditing) {
      if (internalValue === (originalValue || '')) {
        toast.info('No changes detected');
        setIsEditing(false);
        return;
      }
      try {
        await onSave(name, internalValue);
        setIsEditing(false);
      } catch (err) {
        toast.error(`Failed to update ${label}: ${err.message}`);
      }
    } else {
      setIsEditing(true);
    }
  };

  /* Helper to render options whether they are string or object {label,value} */
  const renderOptions = () =>
    options?.map((opt) => {
      if (typeof opt === 'string') {
        return (
          <option key={opt} value={opt}>
            {opt}
          </option>
        );
      } else if (typeof opt === 'object' && opt !== null) {
        return (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        );
      }
      return null;
    });

  return (
    <Form.Group controlId={name} className="mb-3 position-relative">
      {isEditMode && onSave && (
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="mb-0">{label}</Form.Label>
          <span
            role="button"
            tabIndex={0}
            onClick={toggleEdit}
            style={{ cursor: isSaving ? 'wait' : 'pointer', color: '#0d6efd' }}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save' : 'Edit'}
          </span>
        </div>
      )}

      {!isEditMode || !onSave ? (
        <>
          <Form.Label>{label}</Form.Label>
          {type === 'select' ? (
            <Form.Select name={name} value={value || ''} disabled={disabled} onChange={onChange}>
              <option value="">{selectPlaceholder}</option>
              {renderOptions()}
            </Form.Select>
          ) : (
            <div className="position-relative">
              <Form.Control
                name={name}
                value={value || ''}
                type={type}
                disabled={disabled}
                onChange={onChange}
                placeholder={placeholder}
              />
              {appendContent}
            </div>
          )}
        </>
      ) : (
        <>
          {type === 'select' ? (
            <Form.Select
              value={internalValue}
              disabled={!isEditing}
              onChange={(e) => setInternalValue(e.target.value)}
            >
              {/* Added empty option here for proper prefill */}
              <option value="">{selectPlaceholder}</option>
              {renderOptions()}
            </Form.Select>
          ) : (
            <Form.Control
              value={internalValue}
              disabled={!isEditing}
              onChange={(e) => setInternalValue(e.target.value)}
            />
          )}
          {appendContent}
        </>
      )}

      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
};

/* ---------------------------------------------------------------------
   DocumentUploadSection Component
------------------------------------------------------------------------ */
const DocumentUploadSection = ({
  label,
  existingDocuments = [],
  onUpload,
  onRemove,
  isEditMode,
  target = 'identity',
  options = [],
}) => {
  const [documentType, setDocumentType] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleUploadClick = () => {
    if (!documentType.trim()) {
      toast.error('Document type is required');
      return;
    }
    if (!selectedFiles.length) {
      toast.error('Select at least one file');
      return;
    }
    onUpload(selectedFiles, documentType.trim(), target);
    setSelectedFiles([]);
    setDocumentType('');
  };

  const formatFilename = (name) => {
    if (!name) return '';
    return name.length > 40 ? name.slice(0, 30) + '...' : name;
  };

  return (
    <div className="mb-4">
      <Form.Label className="fw-semibold mb-2 d-block">{label}</Form.Label>

      {existingDocuments.length > 0 && (
        <ListGroup className="mb-3">
          {existingDocuments.map((doc) => (
            <ListGroup.Item
              key={doc.id}
              className="d-flex justify-content-between align-items-center small"
            >
              <div className="d-flex align-items-center">
                <FileEarmarkText size={16} className="me-2" />
                <div>
                  <div className="text-truncate" style={{ maxWidth: 420 }}>
                    {formatFilename(doc.originalName || 'Unknown')}
                  </div>
                  <small className="text-muted">Type: {doc.documentType}</small>
                </div>
              </div>

              {isEditMode && (
                <XCircle
                  role="button"
                  className="text-danger"
                  size={18}
                  onClick={() => onRemove(doc.id, target)}
                />
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      <Form.Select
        className="mb-2"
        value={documentType}
        onChange={(e) => setDocumentType(e.target.value)}
      >
        <option value="">Select document type</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>

      <Form.Control
        type="file"
        multiple
        className="mb-2"
        onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
      />

      <Button
        variant="primary"
        disabled={!documentType || selectedFiles.length === 0}
        onClick={handleUploadClick}
      >
        Attach
      </Button>
    </div>
  );
};

/* ---------------------------------------------------------------------
   PersonalForm Component
------------------------------------------------------------------------ */
const PersonalForm = ({
  formData = {},
  errors = {},
  handleInputChange,
  userId,
  patchProfileProperties,
  refetch,
  isEditMode,
}) => {

  const [originalValues, setOriginalValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [showSsn, setShowSsn] = useState(false);

  const {
    uploadFile,
    uploading,
    error: uploadError,
    submitIdentityVerification,
    submitWorkAuthorization,
  } = useFileUpload();

  // Store uploaded files per target
  const [uploadedFiles, setUploadedFiles] = useState({
    identity: [],
    workAuth: [],
  });

  useEffect(() => {
    const keys = ['birthDate', 'gender', 'ssn', 'status'];
    const orig = {};
    keys.forEach((k) => (orig[k] = formData[k] || ''));
    setOriginalValues(orig);

    // Initialize uploadedFiles from formData
    setUploadedFiles({
      identity: formData.userIdentityVerification
        ? (formData.userIdentityVerification.idDocumentIds || []).map((id) => ({
            id,
            documentType: formData.userIdentityVerification.identificationDocumentType || 'UNKNOWN',
            originalName: id,
          }))
        : [],
      workAuth: formData.userWorkAuthorization
        ? (formData.userWorkAuthorization.idDocumentIds || []).map((id) => ({
            id,
            documentType: formData.userWorkAuthorization.workAuthorizationType || 'UNKNOWN',
            originalName: id,
          }))
        : [],
    });
  }, [formData]);

  /* Save simple fields */
  const handleFieldSave = async (fieldName, value) => {
    setSavingFields((p) => ({ ...p, [fieldName]: true }));
    try {
      await patchProfileProperties(userId, [{ name: fieldName, value }]);
      handleInputChange({ target: { name: fieldName, value } });
      if (refetch) await refetch();
      toast.success(`${fieldName} updated`);
    } catch (err) {
      toast.error(err.message || 'Failed to save field');
    } finally {
      setSavingFields((p) => {
        const copy = { ...p };
        delete copy[fieldName];
        return copy;
      });
    }
  };

  /* Attach/Upload handler */
  const handleUpload = async (files, documentType, target) => {
    if (!documentType) {
      toast.error('Document type is required');
      return;
    }

    if (!files || files.length === 0) {
      toast.error('No files selected');
      return;
    }

    if (userId) {
      // Edit mode: upload immediately
      try {
        toast.info(`Uploading ${files.length} file(s)...`);

        const uploadedFileIds = [];
        const newDocuments = [];

        for (const file of files) {
          const isImg = /\.(jpe?g|png|webp|gif|heic)$/i.test(file.name);
          const fileType = isImg ? 'PHOTO' : 'DOCUMENT';

          const result = await uploadFile(userId, file, {
            purpose: 'USER_PROFILE',
            fileType,
          });

          uploadedFileIds.push(result.fileId);
          newDocuments.push({
            id: result.fileId,
            documentType,
            originalName: file.name,
          });
        }

        // Update local state
        const updatedDocs = [...uploadedFiles[target], ...newDocuments];
        setUploadedFiles((prev) => ({
          ...prev,
          [target]: updatedDocs,
        }));

        // Submit based on target
        if (target === 'identity') {
          await submitIdentityVerification(userId, documentType, uploadedFileIds);
        } else {
          await submitWorkAuthorization(userId, documentType, uploadedFileIds);
        }

        toast.success(`${files.length} file(s) uploaded and submitted`);

        if (refetch) {
          await refetch();
        }
      } catch (err) {
        console.error('Upload or verification error:', err);
        toast.error(err.message || 'Failed to upload and submit documents');
      }
    } else {
      // Add mode: attach locally
      const newDocuments = files.map(file => ({
        id: null,
        documentType,
        originalName: file.name,
        file,
      }));

      setUploadedFiles((prev) => ({
        ...prev,
        [target]: [...prev[target], ...newDocuments],
      }));

      toast.info('Documents attached for upload after user creation');
    }
  };

  /* Remove handler */
  const handleRemove = (fileId, target) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [target]: prev[target].filter((doc) => doc.id !== fileId),
    }));

    // TODO: Add backend deletion call here if you have one

    toast.info('Document removed (locally)');
  };

  /* SSN visibility toggle */
  const identityOptions = [
    { label: 'Passport', value: 'PASSPORT' },
    { label: "Driver's License", value: 'DRIVERS_LICENSE' },
    { label: 'School ID', value: 'SCHOOL_ID' },
    { label: 'Government ID', value: 'GOVERNMENT_ID' },
  ];

  const workOptions = [
    { label: 'Citizen', value: 'CITIZEN' },
    { label: 'Green Card', value: 'GREEN_CARD' },
    { label: 'H1B', value: 'H1B' },
    { label: 'CPT', value: 'CPT' },
    { label: 'OPT', value: 'OPT' },
    { label: 'Student', value: 'STUDENT' },
    { label: 'Visitor', value: 'VISITOR' },
    { label: 'Other', value: 'OTHER' },
  ];

  // Gender options updated to object form with empty placeholder option
  const genderOptions = [
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Other', value: 'OTHER' },
  ];

  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Disabled', value: 'DISABLED' },
    { label: 'Suspended', value: 'SUSPENDED' },
    { label: 'Deleted', value: 'DELETED' },
  ];

  const ssnAppend = (
    <Button
      variant="link"
      onClick={() => setShowSsn((s) => !s)}
      className="position-absolute top-50 end-0 translate-middle-y text-muted p-2"
      tabIndex={-1}
    >
      {showSsn ? <EyeSlash size={18} /> : <Eye size={18} />}
    </Button>
  );

  return (
    <>
      {uploading && <p className="text-info">Uploading…</p>}
      {uploadError && <p className="text-danger">{uploadError}</p>}

      <Row className="g-4">
        {/* Birth Date */}
        <Col md={6}>
          <EditableField
            label="Date of Birth"
            name="birthDate"
            type="date"
            value={formData.birthDate || ''}
            error={errors.birthDate}
            onChange={handleInputChange}
            onSave={
              isEditMode
                ? (name, value) => {
                    console.log('Sending birthDate:', value);
                    return handleFieldSave(name, value);
                  }
                : null
            }
            originalValue={originalValues.birthDate}
            isSaving={savingFields.birthDate}
            isEditMode={isEditMode}
            placeholder="YYYY-MM-DD"
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="Gender"
            name="gender"
            type="select"
            options={genderOptions}
            value={formData.gender || ''}
            error={errors.gender}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.gender}
            isSaving={savingFields.gender}
            isEditMode={isEditMode}
          />
        </Col>

        {/* SSN */}
        <Col md={6}>
          <EditableField
            label="SSN"
            name="ssn"
            type={showSsn ? 'text' : 'password'}
            value={formData.ssn || ''}
            error={errors.ssn}
            onChange={handleInputChange}
            onSave={null}
            originalValue={originalValues.ssn}
            isSaving={savingFields.ssn}
            isEditMode={false}
            placeholder="123-45-6789"
            disabled={isEditMode}
            appendContent={ssnAppend}
          />
        </Col>

        {/* Status */}
        <Col md={6}>
          <EditableField
            label="Status"
            name="status"
            type="select"
            options={statusOptions}
            value={formData.status}
            originalValue={originalValues.status}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            isSaving={savingFields.status}
            error={errors.status}
            isEditMode={isEditMode}
          />
        </Col>

        {/* Identification Documents */}
        <Col md={12}>
          <DocumentUploadSection
            label="Identification Documents"
            isEditMode={isEditMode}
            target="identity"
            options={identityOptions}
            onUpload={handleUpload}
            onRemove={handleRemove}
            existingDocuments={uploadedFiles.identity}
          />
        </Col>

        {/* Work Authorization Documents */}
        <Col md={12}>
          <DocumentUploadSection
            label="Work Authorization Documents"
            isEditMode={isEditMode}
            target="workAuth"
            options={workOptions}
            onUpload={handleUpload}
            onRemove={handleRemove}
            existingDocuments={uploadedFiles.workAuth}
          />
        </Col>
      </Row>
    </>
  );
};

export default PersonalForm;